import {useRefreshMe} from './hooks/session/useRefreshMe';
import {useSession} from './hooks/session/SessionProvider';
import {useSessionExpiry} from './hooks/session/useSessionExpiry';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  Typography,
  Table,
  TableRow,
  TableBody,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import {getCurrentUser} from 'aws-amplify/auth';
import {ConsoleLogger, Hub} from 'aws-amplify/utils';
import {isFuture} from 'date-fns';
import {OptionsWithExtraProps, useSnackbar} from 'notistack';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';

import {useMixpanelInstance} from './analytics/useMixpanelInstance';
import {useCrisp} from './components/CrispProvider';
import {useUpdateNotificationsSettingsMutation} from './hooks/notifications/mutations/useUpdateNotificationsSettingsMutation';
import {useNotificationsSettingsQuery} from './hooks/notifications/queries/useNotificationsSettingsQuery';
import {usePremium, useShouldShowTrialUI, useUser} from './hooks/session/useSessionSelectors';
import {TableCell} from './views/Settings/Profile';

type LoginRequiredRouteState = {
  checkingSession: boolean;
};

type NotificationSettingsState = {
  openDialog: boolean;
  settings: {
    refreshTokenExpiration: boolean;
    extensionPing: boolean;
    marketingEmails: boolean;
    successfulPost: boolean;
    failedPost: boolean;
    proofreadingPost: boolean;
  };
};

const logger = new ConsoleLogger('LoginRequiredRoute');

export function LoginRequiredRoute() {
  const {i18n, t} = useTranslation();
  const navigate = useNavigate();
  const refreshMe = useRefreshMe();
  const {refreshSession} = useSession();
  const location = useLocation();
  const mp = useMixpanelInstance();
  const {enqueueSnackbar} = useSnackbar();
  const {crisp} = useCrisp();
  // Session qui expire EN COURS d'utilisation : message, déconnexion, retour à `/signin`.
  useSessionExpiry();

  const [state, setState] = useState<LoginRequiredRouteState>({
    checkingSession: true,
  });
  const {checkingSession} = state;

  const [notificationSettings, setNotificationSettings] = useState<NotificationSettingsState>({
    openDialog: false,
    settings: {
      refreshTokenExpiration: true,
      extensionPing: true,
      marketingEmails: true,
      successfulPost: true,
      failedPost: true,
      proofreadingPost: true,
    },
  });

  const user = useUser();
  const premium = usePremium();
  const shouldShowTrialUI = useShouldShowTrialUI(); // <--- AJOUT ICI
  // Lus une fois la session vérifiée : un document sans `_id` ouvre le dialogue de premier réglage.
  const notificationsSettingsQuery = useNotificationsSettingsQuery({enabled: !checkingSession && user !== undefined});
  const updateNotificationsSettings = useUpdateNotificationsSettingsMutation();

  const handleNotificationChange = (key: keyof NotificationSettingsState['settings'], value: boolean) => {
    setNotificationSettings((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [key]: value,
      },
    }));
  };

  const saveNotificationSettings = async () => {
    try {
      await updateNotificationsSettings.mutateAsync(notificationSettings.settings);
    } catch {
      // Refus affiché par le filet global des mutations : le dialogue reste ouvert.
      return;
    }
    setNotificationSettings((prev) => ({...prev, openDialog: false}));
  };

  useEffect(() => {
    const response = notificationsSettingsQuery.data;
    if (response === undefined) {
      return;
    }
    if (!response._id) {
      setNotificationSettings((prev) => ({...prev, openDialog: true}));
    } else {
      setNotificationSettings((prev) => ({
        ...prev,
        settings: {
          refreshTokenExpiration: response.refreshTokenExpiration ?? true,
          extensionPing: response.extensionPing ?? true,
          marketingEmails: response.marketingEmails ?? true,
          successfulPost: response.successfulPost ?? true,
          failedPost: response.failedPost ?? true,
          proofreadingPost: response.proofreadingPost ?? true,
        },
      }));
    }
  }, [notificationsSettingsQuery.data]);

  useEffect(() => {
    getCurrentUser()
      .then(async () => {
        await refreshSession();
        refreshMe();
        // `dispatch(fetchTeams())` retiré : `useTeamsQuery` charge la liste au
        // montage du premier consommateur — c'est-à-dire dès l'affichage de la
        // barre latérale, qui liste les comptes. Les réglages d'e-mails, eux, sont
        // lus par `useNotificationsSettingsQuery` dès que `checkingSession` retombe.
        setState((f) => ({...f, checkingSession: false}));
      })
      .catch((err) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        console.warn({err});
        // La query suit la connexion : le lien `?promo=` de l'email winback doit survivre au login.
        navigate('/signin', {state: {from: location.pathname + location.search}});
        setState((f) => ({...f, checkingSession: false}));
      });

    const callback = Hub.listen('auth', ({payload}) => {
      switch (payload.event) {
        case 'signedIn':
          console.log('user have been signedIn successfully.');
          break;
        case 'signedOut':
          console.log('user have been signedOut successfully.');
          mp.logout();
          break;
        case 'tokenRefresh':
          console.log('auth tokens have been refreshed.');
          break;
        // `tokenRefresh_failure` : traité par `useSessionExpiry`, seul écouteur de cet événement.
        case 'signInWithRedirect':
          console.log('signInWithRedirect API has successfully been resolved.');
          break;
        case 'signInWithRedirect_failure':
          console.log('failure while trying to resolve signInWithRedirect API.');
          break;
        case 'customOAuthState':
          logger.info('custom state returned from CognitoHosted UI');
          break;
      }
    });
    crisp.showIcon();
    return () => {
      callback();
    };
  }, []);

  useEffect(() => {
    if (user) {
      i18n.changeLanguage(user.locale);
      Sentry.setUser({
        id: user._id,
        cognitoId: user.cognitoId,
        vanityName: user.vanityName,
      });
      crisp.trackUser(user, premium);
      if (
        shouldShowTrialUI &&
        user?.earlyAdoptersPremiumExpireAt !== undefined &&
        isFuture(user.earlyAdoptersPremiumExpireAt)
      ) {
        const options: OptionsWithExtraProps<'freeTrial'> = {
          preventDuplicate: true,
          variant: 'freeTrial',
        };
        enqueueSnackbar('freeTrial', options);
      }
    }
  }, [user, premium, shouldShowTrialUI]);

  // `login` part une fois par utilisateur et par chargement de page, APRÈS identify (donc sur
  // l'identité connectée). Il partait aussi à chaque changement de statut Premium, et une seconde
  // fois, anonyme, depuis l'écran de connexion.
  const loginTrackedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    // session prête + user chargé
    if (!checkingSession && user) {
      mp.identifyUser(user, {
        Platform: 'Web',
        'Account Status': premium ? 'Paid' : 'Free',
        // 'Company Name': ... // si tu veux pousser côté front
        // 'Company Size': ...
      });
      if (loginTrackedFor.current !== user._id) {
        loginTrackedFor.current = user._id;
        mp.track('login', {source: 'web'});
      }
    }
    // Pas de reset quand il n'y a pas d'utilisateur : une visite non connectée n'est pas une
    // déconnexion, et l'identifiant anonyme doit survivre jusqu'à la première connexion pour que
    // `signup-form-submitted` et les pages vues avant connexion soient rattachés au compte. Le reset
    // ne vit que sur `signedOut` et les logouts explicites.
  }, [checkingSession, user?._id, premium, mp]);

  if (user !== undefined && !checkingSession) {
    return (
      <>
        <Outlet />
        <Dialog open={notificationSettings.openDialog}>
          <DialogTitle>{t('settings.notification.title')}</DialogTitle>
          <DialogContent>
            <Table sx={{minWidth: '36rem'}}>
              <Box component="thead">
                <TableRow>
                  <TableCell width="100%" padding={[1.5, 3, 1.5, 0.5]}>
                    {t('settings.notifications.title')}
                  </TableCell>
                  <TableCell align="center" padding={[1.5, 3, 1.5, 3]}>
                    {t('settings.notifications.email')}
                  </TableCell>
                </TableRow>
              </Box>
              <TableBody>
                <TableRow>
                  <TableCell padding={[1, 1, 1, 0.5]}>
                    <Box lineHeight={1.4}>
                      <Typography sx={{fontSize: '14px', lineHeight: '20px'}}>
                        {t('settings.notifications.expiredlinkedin.title')}
                      </Typography>
                      <Typography sx={{fontSize: '12px', lineHeight: '20px', color: 'text.secondary'}}>
                        {t('settings.notifications.expiredlinkedin.description')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                    <Switch
                      checked={notificationSettings.settings.refreshTokenExpiration}
                      onChange={(e, checked) => handleNotificationChange('refreshTokenExpiration', checked)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell padding={[1, 1, 1, 0.5]}>
                    <Box lineHeight={1.4}>
                      <Typography sx={{fontSize: '14px', lineHeight: '20px'}}>
                        {t('settings.notifications.extensionping.title')}
                      </Typography>
                      <Typography sx={{fontSize: '12px', lineHeight: '20px', color: 'text.secondary'}}>
                        {t('settings.notifications.extensionping.description')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                    <Switch
                      checked={notificationSettings.settings.extensionPing}
                      onChange={(e, checked) => handleNotificationChange('extensionPing', checked)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell padding={[1, 1, 1, 0.5]}>
                    <Box lineHeight={1.4}>
                      <Typography sx={{fontSize: '14px', lineHeight: '20px'}}>
                        {t('settings.notifications.scheduledpost.title')}
                      </Typography>
                      <Typography sx={{fontSize: '12px', lineHeight: '20px', color: 'text.secondary'}}>
                        {t('settings.notifications.scheduledpost.description')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                    <Switch
                      checked={notificationSettings.settings.successfulPost}
                      onChange={(e, checked) => handleNotificationChange('successfulPost', checked)}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell padding={[1, 1, 1, 0.5]}>
                    <Box lineHeight={1.4}>
                      <Typography sx={{fontSize: '14px', lineHeight: '20px'}}>
                        {t('settings.notifications.marketingemails.title')}
                      </Typography>
                      <Typography sx={{fontSize: '12px', lineHeight: '20px', color: 'text.secondary'}}>
                        {t('settings.notifications.marketingemails.description')}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center" padding={[1, 1, 1, 0.5]}>
                    <Switch
                      checked={notificationSettings.settings.marketingEmails}
                      onChange={(e, checked) => handleNotificationChange('marketingEmails', checked)}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button onClick={saveNotificationSettings} variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  } else {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
        <CircularProgress />
      </Box>
    );
  }
}
